/**
 * The people an organization has chosen to name on its own page (GH-770, migration 0062).
 *
 * ## Why this is the quietest block on the screen
 *
 * A named individual attached to a named faith-based organization on a public, uncredentialed page
 * *is* an inference about that person's religion, made easier and made permanent — and CLAUDE.md
 * treats religious affiliation as special-category data wherever it is inferable. The person named
 * is not a user of this product and has consented to nothing here; the organization asserted the
 * permission on their behalf. That is what DEC-044 is open about, and until it closes the shape of
 * this block is the mitigation: name, role, nothing else, no links, and no way to reach the person
 * from here.
 *
 * ## What it therefore does not render
 *
 * **No photograph.** A face beside a name is the inference drawn rather than merely implied. There
 * is no column for one — see the migration.
 *
 * **No monogram, no avatar, no coloured tile.** The directory's `OrgMark` gives an organization
 * with no logo a tinted initial, and doing the same here would manufacture a portrait for somebody
 * who supplied none.
 *
 * **No email, no phone, no `mailto:`.** The public reads behind this page are built on the
 * guarantee that none of them reaches a contact (`services/api/src/directory-routes.ts`); a
 * clickable address here would end that regardless of where the string came from.
 *
 * **No link of any kind.** Not to a profile, not to a search, not to the organization's site.
 * Every link is a route to more about the person, and this page is deliberately a dead end.
 *
 * ## Absent, not empty
 *
 * An organization that has named nobody renders no heading and no block. "Nobody listed" is a
 * sentence about people, and one that most organizations would be having said about them because
 * a console field is blank rather than because anything is true.
 *
 * `dir="auto"` on both strings: a name and a role are the organization's own words in the
 * organization's own script, and a Hebrew or Arabic name mixed into a left-to-right page reorders
 * wrongly without it.
 */
import type { OrganizationPerson } from "@givehere/contracts";

export function OrgPeople({ people }: { people: readonly OrganizationPerson[] }) {
  if (people.length === 0) return null;

  return (
    <section className="landing__block">
      <h2 className="landing__section">People</h2>
      <ul className="orgpeople">
        {people.map((person, index) => (
          /*
            Keyed by name and position because the wire carries **no id**. That is not an oversight
            in the payload: an identifier for a person is a handle to correlate them across
            requests, and this list is presentational. The index is stable for a list that is
            replaced wholesale and never reordered on the client.
          */
          <li className="orgpeople__row" key={`${index}-${person.name}`}>
            <span className="orgpeople__name" dir="auto">
              {person.name}
            </span>
            <span className="orgpeople__role" dir="auto">
              {person.roleText}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
